import config from '../config';

// ==========================================
// Google Calendar API Functions
// ==========================================

/**
 * Fetch available time slots from Google Calendar
 * Requires public calendar with free/busy visibility
 */
export const fetchAvailableSlots = async (selectedDate) => {
  const { apiKey, calendarId, timeZone, meetingDuration } = config.googleCalendar;
  
  // Set time range for the selected date
  const startOfDay = new Date(selectedDate);
  startOfDay.setHours(9, 0, 0, 0); // Start at 9 AM
  
  const endOfDay = new Date(selectedDate);
  endOfDay.setHours(19, 0, 0, 0); // End at 7 PM
  
  try {
    // Fetch busy times using Google Calendar FreeBusy API
    const response = await fetch(
      `https://www.googleapis.com/calendar/v3/freeBusy?key=${apiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          timeMin: startOfDay.toISOString(),
          timeMax: endOfDay.toISOString(),
          timeZone: timeZone,
          items: [{ id: calendarId }],
        }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch calendar availability');
    }

    const data = await response.json();
    const busySlots = data.calendars[calendarId]?.busy || [];
    
    // Generate all possible 30-min slots
    const allSlots = generateTimeSlots(startOfDay, endOfDay, meetingDuration);
    
    // Filter out busy slots
    const availableSlots = allSlots.filter(slot => {
      const slotStart = new Date(slot.start);
      const slotEnd = new Date(slot.end);
      
      return !busySlots.some(busy => {
        const busyStart = new Date(busy.start);
        const busyEnd = new Date(busy.end);
        return slotStart < busyEnd && slotEnd > busyStart;
      });
    });

    return availableSlots;
  } catch (error) {
    console.error('Error fetching calendar slots:', error);
    // Return default slots if API fails
    return generateTimeSlots(startOfDay, endOfDay, meetingDuration);
  }
};

/**
 * Generate time slots for a given range
 */
const generateTimeSlots = (startTime, endTime, durationMinutes) => {
  const slots = [];
  const current = new Date(startTime);
  
  while (current < endTime) {
    const slotEnd = new Date(current.getTime() + durationMinutes * 60 * 1000);
    
    if (slotEnd <= endTime) {
      slots.push({
        start: current.toISOString(),
        end: slotEnd.toISOString(),
        displayTime: formatTime(current),
      });
    }
    
    current.setMinutes(current.getMinutes() + durationMinutes);
  }
  
  return slots;
};

/**
 * Format time for display
 */
const formatTime = (date) => {
  return date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Create calendar event
 */
export const createCalendarEvent = async (eventDetails) => {
  const { apiKey, calendarId, timeZone } = config.googleCalendar;
  
  const event = {
    summary: `Discovery Call - ${eventDetails.businessName}`,
    description: `
      Contact: ${eventDetails.name}
      Phone: ${eventDetails.phone}
      Industry: ${eventDetails.industry}
      Team Size: ${eventDetails.teamSize}
      Pain Point: ${eventDetails.painPoint}
      Selected Tool: ${eventDetails.selectedTool}
      Budget: ${eventDetails.budget}
    `,
    start: {
      dateTime: eventDetails.slotStart,
      timeZone: timeZone,
    },
    end: {
      dateTime: eventDetails.slotEnd,
      timeZone: timeZone,
    },
    attendees: [
      { email: eventDetails.email || '' },
    ],
  };

  try {
    const response = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/${calendarId}/events?key=${apiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to create calendar event');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating calendar event:', error);
    throw error;
  }
};


// ==========================================
// Google Sheets API Functions
// ==========================================

// Track progress at each step
export const trackProgress = async (formData, currentStep, sessionId, isCompleted = false) => {
  const { webhookUrl } = config.googleSheets;
  
  const payload = {
    sessionId: sessionId,
    timestamp: formData.startTime || new Date().toISOString(),
    name: formData.name || '',
    email: formData.email || '',
    phone: formData.phoneNumber ? `${formData.countryCode}${formData.phoneNumber}` : '',
    industry: formData.industry || '',
    industryOther: formData.industryOther || '',
    teamSize: formData.teamSize || '',
    website: formData.website || '',
    painPoint: formData.painPoint || '',
    painPointOther: formData.painPointOther || '',
    businessDescription: formData.businessDescription || '',
    selectedTools: formData.selectedTools?.join(', ') || '',
    budget: formData.budget || '',
    readiness: formData.readiness || '',
    scheduledDate: formData.scheduledDate || '',
    scheduledTime: formData.scheduledTime || '',
    slotStart: formData.slotStart || '',
    slotEnd: formData.slotEnd || '',
  };

  try {
    await fetch(webhookUrl, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return { success: true };
  } catch (error) {
    console.error('Error tracking progress:', error);
    return { success: false };
  }
};

/**
 * Submit form data to Google Sheets via Apps Script webhook
 */
export const submitToGoogleSheets = async (formData, sessionId) => {
  return trackProgress(formData, 'Completed', sessionId, true);
};


// ==========================================
// WhatsApp Business API Functions
// ==========================================

/**
 * Send WhatsApp confirmation message
 */
export const sendWhatsAppConfirmation = async (formData) => {
  const { apiUrl, phoneNumberId, accessToken, templateName } = config.whatsAppBusiness;
  
  const phoneNumber = `${formData.countryCode}${formData.phoneNumber}`.replace('+', '');
  
  const payload = {
    messaging_product: 'whatsapp',
    to: phoneNumber,
    type: 'template',
    template: {
      name: templateName,
      language: {
        code: 'en',
      },
      components: [
        {
          type: 'body',
          parameters: [
            { type: 'text', text: formData.name },
            { type: 'text', text: formData.scheduledDate },
            { type: 'text', text: formData.scheduledTime },
            { type: 'text', text: formData.selectedToolName || 'Discovery Call' },
          ],
        },
      ],
    },
  };

  try {
    const response = await fetch(`${apiUrl}/${phoneNumberId}/messages`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error('Failed to send WhatsApp message');
    }

    return await response.json();
  } catch (error) {
    console.error('Error sending WhatsApp message:', error);
    // Don't throw - WhatsApp confirmation is optional
    return { success: false, error: error.message };
  }
};

/**
 * Send simple WhatsApp text message (fallback)
 */
export const sendWhatsAppTextMessage = async (formData) => {
  const { apiUrl, phoneNumberId, accessToken } = config.whatsAppBusiness;
  
  const phoneNumber = `${formData.countryCode}${formData.phoneNumber}`.replace('+', '');
  
  const message = `Hi ${formData.name}! 👋

Your Discovery Call is confirmed!

📅 Date: ${formData.scheduledDate}
⏰ Time: ${formData.scheduledTime}
🛠️ Tool: ${formData.selectedToolName || 'AI Automation Solution'}

We'll call you on this number. Looking forward to helping automate your business!

- Team Galific`;

  const payload = {
    messaging_product: 'whatsapp',
    to: phoneNumber,
    type: 'text',
    text: {
      body: message,
    },
  };

  try {
    const response = await fetch(`${apiUrl}/${phoneNumberId}/messages`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error('Failed to send WhatsApp message');
    }

    return await response.json();
  } catch (error) {
    console.error('Error sending WhatsApp message:', error);
    return { success: false, error: error.message };
  }
};


// ==========================================
// Utility Functions
// ==========================================

/**
 * Get next 3 available dates (excluding Sundays) to prevent distant bookings
 */
export const getNextSevenDays = () => {
  const dates = [];
  const today = new Date();
  let currentDate = new Date(today);
  currentDate.setDate(currentDate.getDate() + 1); // Start from tomorrow
  
  while (dates.length < 3) {
    if (currentDate.getDay() !== 0) { // Exclude Sundays
      dates.push({
        date: new Date(currentDate),
        displayDate: formatDate(currentDate),
        dayName: getDayName(currentDate),
      });
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return dates;
};

const formatDate = (date) => {
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
  });
};

const getDayName = (date) => {
  return date.toLocaleDateString('en-IN', {
    weekday: 'short',
  });
};

/**
 * Format phone number for display
 */
export const formatPhoneNumber = (countryCode, number) => {
  return `${countryCode} ${number}`;
};
